import { Component, OnChanges, OnDestroy, input, output, inject } from '@angular/core';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {Order} from '@app/checkout/models/order-request.model';
import {resetCheckoutStatus, saveTestOrder} from '@app/checkout/state/checkout.actions';
import {selectCheckoutStatus} from '@app/checkout/state/checkout.selector';
import {ICustomerOrganization, ICustomerProfileState} from '@app/shared/models/header.model';
import {IProductAttribute} from '@app/shared/models/product.model';
import {selectCustomerOrganization, selectCustomerProfile} from '@app/shared/state/header/header.selector';
import {Store} from '@ngrx/store';
import {Subject, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { Accordion, AccordionPanel, AccordionHeader, AccordionContent } from 'primeng/accordion';
import { Ripple } from 'primeng/ripple';
import { TestSelectorComponent } from '../../../shared/components/service-cards/test-selector/test-selector.component';
import { ServiceCardTitleComponent } from '../../../shared/components/service-cards/service-card-title/service-card-title.component';
import { Button } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-product-variant',
    templateUrl: './product-variant.component.html',
    styleUrls: ['./product-variant.component.scss'],
    imports: [
        Bind,
        Accordion,
        AccordionPanel,
        Ripple,
        AccordionHeader,
        AccordionContent,
        TestSelectorComponent,
        ServiceCardTitleComponent,
        Button,
        TranslatePipe,
    ],
})
export class ProductVariantComponent implements OnDestroy, OnChanges {
  private readonly store = inject(Store);

  readonly productAttributes = input.required<IProductAttribute[]>();
  readonly productID = input.required<number>();
  readonly variantID = input.required<number>();
  readonly highlightProduct = output<boolean>();
  organizationId: number | null = null;
  doctorId: string | null = null;
  showButton = false;
  selectedIndex: {[key: string]: number[]} = {};
  private readonly unsubscribe$ = new Subject<void>();
  isOrderCreating = false;
  productIndex = -1;

  constructor() {
    this.store
      .select(selectCustomerOrganization)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((value: ICustomerOrganization | null) => {
          if (value?.organizationId) {
            this.organizationId = value.organizationId;
          }
        })
      )
      .subscribe();

    this.store
      .select(selectCustomerProfile)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((value: ICustomerProfileState | null) => {
          if (value?.doctorId) {
            this.doctorId = value.doctorId;
          }
        })
      )
      .subscribe();
    this.getCheckoutOrderStatus();
  }

  ngOnChanges(): void {
    this.productIndex = this.productAttributes().findIndex(attr =>
      attr.productWithAttributes?.some(va => va.id === this.variantID())
    );
    this.highlightProduct.emit(this.productIndex < 0);
  }

  onOrderTest(id: number) {
    this.saveTestOrder(id);
  }

  private saveTestOrder(id: number) {
    this.isOrderCreating = true;
    const orderData = {
      productId: this.productID(),
      productAttributeId: id,
      organizationId: this.organizationId,
      customerDoctorId: this.doctorId,
    } as Order;
    this.store.dispatch(resetCheckoutStatus());
    this.store.dispatch(saveTestOrder({orderRequest: orderData}));
  }

  onGeneClick(event: MouseEvent, productAttr: IProductAttribute, varientIndex: number, attributeIndex: number) {
    event.stopPropagation();
    if (productAttr.noGenes && productAttr.geneContent) {
      if (!this.selectedIndex[varientIndex]) {
        this.selectedIndex[varientIndex] = [];
      }
      if (this.selectedIndex[varientIndex].includes(attributeIndex)) {
        this.selectedIndex[varientIndex].splice(this.selectedIndex[varientIndex].indexOf(attributeIndex), 1);
      } else {
        this.selectedIndex[varientIndex].push(attributeIndex);
      }
    }
  }

  private getCheckoutOrderStatus() {
    this.store
      .select(selectCheckoutStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((status: ECheckoutStatus) => {
          if (status === ECheckoutStatus.Failed) {
            this.isOrderCreating = false;
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
